/**
 * Money rounding — the frontend mirror of `shared/utils/money.py`.
 *
 * The backend quantizes every money value with `ROUND_HALF_EVEN` (banker's
 * rounding) — `quantize_amount` / `quantize_unit_cost` in `shared/utils/money.py`,
 * which is also the mode the `NUMERIC` columns are written with. Any frontend
 * value that is either **persisted** (the server only re-quantizes what it
 * receives, so the frontend's answer is what gets stored) or **compared against**
 * a server-computed value must round the same way, or the two disagree by a cent
 * on an exact-half input.
 *
 * That is the whole reason this module exists: `Math.round(x * 100) / 100` is
 * half-UP, and it was the shape used at every money site in the frontend.
 *
 * These are the only money-rounding primitives in the frontend. Do not hand-roll
 * a second `round2` — import from here. Display-only formatting is a different
 * concern and belongs to the `Localized*Display` components.
 */

/** Money-amount grid: NUMERIC(18, 2). */
export const AMOUNT_DECIMALS = 2;
/** Unit-price / per-unit-cost grid: NUMERIC(18, 4). */
export const UNIT_PRICE_DECIMALS = 4;

/**
 * Multiply `value` by `10 ** exponent` without going through a float multiply.
 *
 * `1.005 * 100` is `100.49999999999999`, so `Math.round` on the product answers
 * as if the input were below the tie — the classic binary-float misfire. Moving
 * the decimal point in the *exponent of the shortest round-tripping decimal
 * string* and re-parsing sidesteps it: string→double parsing is correctly
 * rounded, so `"1.005e2"` reads back as exactly the double nearest 100.5.
 *
 * Going through `toExponential()` (rather than templating `${value}e${exponent}`)
 * is what makes it safe for values already in exponent notation — a float
 * residual like `5.68e-14`, which `"5.68e-14e2"` would turn into `NaN`.
 */
function shiftDecimalPoint(value: number, exponent: number): number {
  if (value === 0 || !Number.isFinite(value)) return value;
  const [mantissa, currentExponent] = value.toExponential().split('e');
  return Number(`${mantissa}e${Number(currentExponent) + exponent}`);
}

/**
 * How much float noise a value is allowed to have drifted off an exact tie and
 * still be treated as sitting on one, in units in the last place of the *shifted*
 * value (the one whose fraction is being tested against 0.5).
 *
 * The backend evaluates a money chain in `Decimal`; we evaluate it in doubles, so
 * an expression whose exact value is a tie arrives here already off it by a few
 * ulps of rounding error. 2 is the measured budget: 1 is too tight — a four-term
 * chain overshoots it and 0.05% of ties are missed — and every value from 2
 * upwards recovers all of them, so 2 is the one that also snaps the fewest
 * genuine near-ties. (840,000 generated cases across the estimate and discount
 * chains, magnitudes 10^0..10^7: at 1 ulp, 605 misses; at 2, none.)
 */
const TIE_TOLERANCE_ULPS = 2;

/**
 * The point at which the tolerance above stops meaning anything and we take the
 * double at face value instead — expressed as a fraction of the digit position
 * being rounded, so it scales with `decimals` rather than assuming cents.
 *
 * A tolerance of 2 ulps is only evidence of a destroyed tie while it stays small
 * against the place we are rounding at. Once it reaches a few percent of that
 * place, the double can no longer resolve which side of the half the exact value
 * was on at all, and "assume it was a tie" is a coin flip that loses more often
 * than it wins. 0.02 is where the measurement puts the crossover.
 *
 * This replaces an earlier absolute magnitude gate (`10 ** (15 - 3)`) that came
 * with a re-parse at 15 significant digits. That pairing was derived for a SINGLE
 * multiply and it *corrupted* chained calculators: the re-parse rewrites the digit
 * the tie lives in whenever the exact product needs a 16th significant digit, so
 * an exact 68344350525.28504 was snapped onto a spurious .285 tie the backend
 * never sees. Testing the distance to the tie instead of rewriting the value
 * removes that whole failure mode, and is at least as accurate at every magnitude
 * measured — see `tools/sweep_money_rounding_twins.py`.
 *
 * What neither approach can do is push the exactly-agreeing band past ~10^10.
 * There, one ulp of an amount's shifted value is already comparable to the
 * smallest offset a genuine value can have from a half-cent, so the two are
 * indistinguishable in a double. That is a property of binary64, not of this
 * constant: past it the answer is decimal arithmetic, not more tuning.
 */
const MAX_TIE_TOLERANCE = 0.02;

/**
 * Round `value` to `decimals` places, half-to-even — the banker's rounding the
 * backend and the DB columns use.
 *
 * Ties go to the even neighbour in both directions and on both signs:
 * `2.5 → 2`, `3.5 → 4`, `-2.5 → -2`, `-3.5 → -4`. A non-finite input is 0 (a
 * cleared or half-typed money field is no money, not `NaN` travelling to the API).
 *
 * Two distinct float hazards have to be cleared to agree with the backend, and
 * the string shift alone only clears the second:
 *
 * 1. **The tie is destroyed before we ever see it.** The backend evaluates the
 *    whole chain in `Decimal`; we evaluate it in doubles, so an expression whose
 *    exact value is a tie can arrive here already off it — `3 * 0.07 * 50 / 100`
 *    is `0.10500000000000002`, not `0.105`, and rounds up to `0.11` where the
 *    backend stores `0.10`. No rounding *mode* can fix that: by this point the
 *    tie is gone. So the tie test carries a tolerance of
 *    {@link TIE_TOLERANCE_ULPS} — a value that close to the half is one that was
 *    *on* the half before the float chain nudged it. The tolerance is relative to
 *    the value, so it needs no magnitude constant; it switches off on its own at
 *    {@link MAX_TIE_TOLERANCE}, past which it can no longer tell.
 * 2. **The tie survives but the shift loses it.** `1.005 * 100` evaluates to
 *    `100.49999…`, one ulp below the half it should be sitting on.
 *    {@link shiftDecimalPoint} re-parses rather than multiplying, avoiding it.
 *
 * What is genuinely beyond reach either way is an amount above ~2.2x10^13, where
 * consecutive doubles are more than a cent apart: the cents are gone before this
 * function is called, and no rounding strategy invents them back. `DECIMAL(18,2)`
 * permits such an amount even though no line item, discount or invoice total
 * approaches it. If money here ever genuinely runs that large the answer is
 * decimal arithmetic, not a wider constant.
 */
export function roundHalfEven(value: number, decimals: number): number {
  if (!Number.isFinite(value)) return 0;
  const shifted = shiftDecimalPoint(value, decimals);
  // Only reachable when the shift overflows to +/-Infinity — a value that large
  // is already far past integer precision, so it is its own rounding.
  if (!Number.isFinite(shifted)) return value;

  // Floored at 1: the fraction under test lives in [0, 1), so a shifted value
  // smaller than that has no smaller noise floor to offer.
  const noise = Math.max(Math.abs(shifted), 1) * Number.EPSILON * TIE_TOLERANCE_ULPS;
  const tolerance = noise <= MAX_TIE_TOLERANCE ? noise : 0;

  const lower = Math.floor(shifted);
  const fraction = shifted - lower;
  let rounded: number;
  // A tie — exactly, or near enough that the difference is float noise rather
  // than signal. Take whichever neighbour is even. `Math.floor` goes toward
  // -Inf, so this is symmetric across zero without a special case for the sign.
  if (Math.abs(fraction - 0.5) <= tolerance) rounded = lower % 2 === 0 ? lower : lower + 1;
  else if (fraction > 0.5) rounded = lower + 1;
  else rounded = lower;

  const result = shiftDecimalPoint(rounded, -decimals);
  // `-0` is a legal double but reads back as "-0.00" through a currency
  // formatter, so normalize it away.
  return result === 0 ? 0 : result;
}

/**
 * Quantize a money **amount** onto the currency minor unit (2 dp), half-to-even.
 * The frontend twin of `shared/utils/money.py::quantize_amount`.
 */
export function quantizeAmount(value: number): number {
  return roundHalfEven(value, AMOUNT_DECIMALS);
}

/**
 * Quantize a **unit price / per-unit cost** onto the 4-dp grid, half-to-even.
 * The frontend twin of `shared/utils/money.py::quantize_unit_cost`.
 */
export function quantizeUnitPrice(value: number): number {
  return roundHalfEven(value, UNIT_PRICE_DECIMALS);
}
